import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Eye, Loader2, AlertTriangle, TrendingUp, Users, Building2,
  DollarSign, Info, ChevronDown, ChevronUp, Sparkles, Shield,
  BarChart3, Target, Scale,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBRL = (val) => {
  if (val == null || isNaN(val)) return 'N/A';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};

const SELO_CONFIG = {
  concentracao: { icon: Target, color: 'bg-[#FFF3E0] text-[#E65100]', label: 'Alta concentração' },
  acima_media: { icon: TrendingUp, color: 'bg-[#FFEBEE] text-[#C62828]', label: 'Acima da média' },
  abaixo_media: { icon: TrendingUp, color: 'bg-[#E3F2FD] text-[#1565C0]', label: 'Abaixo da média' },
  reeleicao_patrimonio: { icon: Shield, color: 'bg-[#F3E5F5] text-[#6A1B9A]', label: 'Reeleição' },
  mudanca_partido: { icon: Users, color: 'bg-[#FFF8E1] text-[#F57F17]', label: 'Mudança partidária' },
  bem_alto_valor: { icon: DollarSign, color: 'bg-[#FFEBEE] text-[#C62828]', label: 'Alto valor' },
};

const GravidadeTag = ({ gravidade }) => {
  const map = {
    alta: 'bg-[#FFEBEE] text-[#C62828]',
    media: 'bg-[#FFF8E1] text-[#F57F17]',
    baixa: 'bg-[#E8F5E9] text-[#2E7D32]',
  };
  return <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${map[gravidade] || map.baixa}`}>{gravidade || 'baixa'}</span>;
};

const AlertCard = ({ alert, onInterpret }) => {
  const cfg = SELO_CONFIG[alert.tipo] || SELO_CONFIG.concentracao;
  const Icon = cfg.icon;
  return (
    <div className="p-3 border border-[#E2E8F0] rounded-md bg-white hover:shadow-sm transition-shadow" data-testid={`alert-card-${alert.tipo}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[#0F1C2E]">{alert.candidato}</span>
            <Badge variant="outline" className="text-[10px] border-[#006030] text-[#006030]">{alert.partido}</Badge>
            <GravidadeTag gravidade={alert.gravidade} />
          </div>
          <p className="text-xs text-[#475569] mt-1">{alert.descricao}</p>
          <Badge className={`mt-1.5 text-[10px] ${cfg.color}`}>{alert.selo}</Badge>
        </div>
      </div>
    </div>
  );
};

const InterpretationPanel = ({ candidate, mediaPatrimonio }) => {
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInterpretation = async () => {
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/spending/interpret`, {
        candidate_name: candidate.nome,
        total_bens: candidate.total_bens || 0,
        bens: candidate.bens || [],
        alertas: candidate.alertas || [],
        media_patrimonio: mediaPatrimonio || 0,
        cargo: candidate.cargo || '',
      }, { timeout: 50000 });
      setInterpretation(resp.data);
    } catch {
      toast.error('Erro ao gerar interpretação.');
    } finally {
      setLoading(false);
    }
  };

  if (!candidate.alertas || candidate.alertas.length === 0) return null;

  const nivelColor = {
    alto: 'border-[#C62828] bg-[#FFEBEE]',
    medio: 'border-[#F57F17] bg-[#FFF8E1]',
    baixo: 'border-[#2E7D32] bg-[#E8F5E9]',
  };

  return (
    <div className="mt-3" data-testid="interpretation-panel">
      {!interpretation && (
        <Button size="sm" onClick={fetchInterpretation} disabled={loading} className="bg-[#006030] hover:bg-[#004020] text-white text-xs" data-testid="interpret-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Analisando...</> : <><Sparkles className="w-3 h-3 mr-1" /> Interpretar com IA</>}
        </Button>
      )}
      {interpretation && (
        <div className={`p-3 rounded-md border ${nivelColor[interpretation.nivel_atencao] || nivelColor.baixo}`} data-testid="interpretation-result">
          {interpretation.destaque && (
            <p className="text-xs font-bold text-[#0F1C2E] mb-1">{interpretation.destaque}</p>
          )}
          <p className="text-xs text-[#475569] leading-relaxed">{interpretation.interpretacao}</p>
          {interpretation.disclaimer && (
            <p className="text-[10px] text-[#94A3B8] mt-2 italic">{interpretation.disclaimer}</p>
          )}
        </div>
      )}
    </div>
  );
};

const FiqueDeOlho = () => {
  const [filters, setFilters] = useState(null);
  const [electionType, setElectionType] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCargo, setSelectedCargo] = useState('');
  const [municipalities, setMunicipalities] = useState([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [loadingMunis, setLoadingMunis] = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [candidatesWithAlerts, setCandidatesWithAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [comparative, setComparative] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showComparative, setShowComparative] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/electoral/filters`, { timeout: 10000 })
      .then(r => setFilters(r.data))
      .catch(() => {});
  }, []);

  const currentType = filters?.election_types?.find(t => t.value === electionType);
  const availableYears = currentType?.years || [];
  const availableCargos = currentType?.cargos || [];

  useEffect(() => {
    if (!selectedState || !selectedYear || electionType !== 'municipal') return;
    setLoadingMunis(true);
    setSelectedMunicipality('');
    axios.get(`${API}/electoral/municipalities`, { params: { state: selectedState, year: parseInt(selectedYear) }, timeout: 15000 })
      .then(r => setMunicipalities(r.data.municipalities || []))
      .catch(() => setMunicipalities([]))
      .finally(() => setLoadingMunis(false));
  }, [selectedState, selectedYear, electionType]);

  useEffect(() => {
    setSelectedYear(''); setSelectedState(''); setSelectedMunicipality('');
    setSelectedCargo(''); setAlerts([]); setStats(null); setComparative(null); setSearched(false);
  }, [electionType]);

  const handleAnalyze = useCallback(async () => {
    if (!selectedYear) { toast.error('Selecione o ano.'); return; }
    setLoading(true);
    setSearched(true);
    setExpandedCandidate(null);

    const cargoVal = selectedCargo ? parseInt(selectedCargo) : (availableCargos[0]?.code || 13);

    try {
      const [analyzeResp, compResp] = await Promise.all([
        axios.post(`${API}/spending/analyze`, {
          year: parseInt(selectedYear),
          state: selectedState,
          municipality: selectedMunicipality,
          cargo: cargoVal,
        }, { timeout: 35000 }),
        axios.get(`${API}/spending/comparative`, {
          params: { year: parseInt(selectedYear), state: selectedState, municipality: selectedMunicipality, cargo: cargoVal },
          timeout: 35000,
        }),
      ]);
      setAlerts(analyzeResp.data.alerts || []);
      setCandidatesWithAlerts(analyzeResp.data.candidates || []);
      setStats(analyzeResp.data.stats || null);
      setComparative(compResp.data);
    } catch {
      toast.error('Erro ao analisar dados.');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedState, selectedMunicipality, selectedCargo, availableCargos]);

  const compChartData = comparative?.comparison?.slice(0, 15).map(c => ({
    name: c.nome?.length > 18 ? c.nome.slice(0, 16) + '…' : c.nome,
    value: c.total_bens,
    partido: c.partido,
  })) || [];

  const COLORS = ['#006030', '#2E7D32', '#F0D000', '#E65100', '#C62828', '#1565C0', '#6A1B9A', '#00838F', '#4E342E', '#F57F17', '#37474F', '#1E3A5F', '#D32F2F', '#388E3C', '#1976D2'];

  return (
    <div className="space-y-6" data-testid="fique-de-olho-page">
      {/* Header */}
      <div className="text-center py-4 md:py-8">
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2" data-testid="fdo-title">
          Fique de Olho
        </h1>
        <p className="text-sm sm:text-base text-[#475569] max-w-2xl mx-auto px-2">
          Inteligência patrimonial. Identifique padrões e compare candidatos com dados oficiais do TSE.
        </p>
      </div>

      {/* Filters */}
      <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="fdo-filters">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-5 h-5 text-[#006030]" />
          <h3 className="text-base font-bold text-[#0F1C2E]">Selecione a eleição</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Tipo</label>
            <Select value={electionType} onValueChange={setElectionType}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="fdo-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                {(filters?.election_types || []).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Ano</label>
            <Select value={selectedYear} onValueChange={setSelectedYear} disabled={!electionType}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="fdo-year"><SelectValue placeholder="Ano" /></SelectTrigger>
              <SelectContent>
                {availableYears.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Estado</label>
            <Select value={selectedState} onValueChange={setSelectedState} disabled={!selectedYear}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="fdo-state"><SelectValue placeholder="UF" /></SelectTrigger>
              <SelectContent>
                {(filters?.states || []).map(s => <SelectItem key={s.uf} value={s.uf}>{s.uf} · {s.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {electionType === 'municipal' && (
            <div>
              <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Município</label>
              <Select value={selectedMunicipality} onValueChange={setSelectedMunicipality} disabled={!selectedState || loadingMunis}>
                <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="fdo-municipality"><SelectValue placeholder={loadingMunis ? 'Carregando…' : 'Município'} /></SelectTrigger>
                <SelectContent>
                  {municipalities.map(m => <SelectItem key={m.code} value={m.code}>{m.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Cargo</label>
            <Select value={selectedCargo || "auto"} onValueChange={v => setSelectedCargo(v === "auto" ? "" : v)} disabled={!electionType}>
              <SelectTrigger className="mt-1 h-9 text-xs border-[#E2E8F0]" data-testid="fdo-cargo"><SelectValue placeholder="Cargo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Automático</SelectItem>
                {availableCargos.map(c => <SelectItem key={c.code} value={String(c.code)}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={handleAnalyze} disabled={!selectedYear || loading} className="mt-4 bg-[#006030] hover:bg-[#004020] text-white font-medium h-10 px-6" data-testid="fdo-analyze-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisando…</> : <><Eye className="w-4 h-4 mr-2" /> Analisar Padrões</>}
        </Button>
      </Card>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="fdo-stats">
          <Card className="p-4 bg-[#006030] text-white rounded-md">
            <p className="text-[10px] font-medium opacity-80 uppercase">Candidatos Analisados</p>
            <p className="text-2xl font-black mt-1">{stats.total_candidatos}</p>
          </Card>
          <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
            <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Com Alertas</p>
            <p className="text-2xl font-black text-[#C62828] mt-1">{stats.candidatos_com_alertas}</p>
          </Card>
          <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
            <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Total de Alertas</p>
            <p className="text-2xl font-black text-[#F57F17] mt-1">{stats.total_alertas}</p>
          </Card>
          <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md">
            <p className="text-[10px] font-bold text-[#94A3B8] uppercase">Patrimônio Médio</p>
            <p className="text-lg font-black text-[#006030] mt-1">{formatBRL(stats.media_patrimonio)}</p>
          </Card>
        </div>
      )}

      {/* Top Alerts */}
      {searched && alerts.length > 0 && (
        <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="fdo-alerts-section">
          <h3 className="text-base font-bold text-[#0F1C2E] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#F57F17]" />
            Top Alertas Identificados
          </h3>
          <div className="space-y-2">
            {alerts.slice(0, 10).map((a, idx) => <AlertCard key={idx} alert={a} />)}
          </div>
          <div className="mt-3 p-3 bg-[#F4F6F8] rounded-md flex items-start gap-2" data-testid="fdo-disclaimer">
            <Info className="w-4 h-4 text-[#94A3B8] shrink-0 mt-0.5" />
            <p className="text-[10px] text-[#94A3B8] leading-relaxed">
              Padrões identificados com base em dados públicos do TSE. Não constituem evidência de irregularidade. Análise de caráter educativo e comparativo.
            </p>
          </div>
        </Card>
      )}

      {/* Candidates with Alerts + Interpretation */}
      {candidatesWithAlerts.length > 0 && (
        <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="fdo-candidates-section">
          <h3 className="text-base font-bold text-[#0F1C2E] mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#006030]" />
            Candidatos com Padrões Identificados ({candidatesWithAlerts.length})
          </h3>
          <div className="space-y-3">
            {candidatesWithAlerts.slice(0, 15).map((c, idx) => (
              <div key={c.id || idx} className="border border-[#E2E8F0] rounded-md overflow-hidden">
                <div
                  onClick={() => setExpandedCandidate(expandedCandidate === c.id ? null : c.id)}
                  className="p-3 flex items-center justify-between cursor-pointer hover:bg-[#FAFBFC] transition-colors"
                  data-testid={`fdo-candidate-${idx}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-[#006030]">{c.alertas_count}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-[#0F1C2E] text-sm truncate">{c.nome}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge variant="outline" className="text-[10px] border-[#006030] text-[#006030]">{c.partido}</Badge>
                        <span className="text-[10px] text-[#94A3B8]">{c.cargo}</span>
                        {c.total_bens != null && <span className="text-[10px] text-[#006030] font-medium">{formatBRL(c.total_bens)}</span>}
                      </div>
                    </div>
                  </div>
                  {expandedCandidate === c.id ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
                </div>
                {expandedCandidate === c.id && (
                  <div className="px-3 pb-3 border-t border-[#E2E8F0] bg-[#FAFBFC]">
                    <div className="space-y-1.5 mt-2">
                      {(c.alertas || []).map((a, ai) => {
                        const cfg = SELO_CONFIG[a.tipo] || SELO_CONFIG.concentracao;
                        return (
                          <div key={ai} className="flex items-start gap-2 text-xs">
                            <Badge className={`text-[10px] shrink-0 ${cfg.color}`}>{a.selo}</Badge>
                            <span className="text-[#475569]">{a.descricao}</span>
                          </div>
                        );
                      })}
                    </div>
                    <InterpretationPanel candidate={c} mediaPatrimonio={stats?.media_patrimonio || 0} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Comparative Chart */}
      {comparative && comparative.comparison?.length > 0 && (
        <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="fdo-comparative-section">
          <button onClick={() => setShowComparative(!showComparative)} className="flex items-center gap-2 text-base font-bold text-[#0F1C2E] hover:text-[#006030] transition-colors w-full text-left" data-testid="fdo-toggle-comparative">
            <BarChart3 className="w-5 h-5 text-[#006030]" />
            Comparativo Patrimonial
            {showComparative ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
          </button>
          {showComparative && (
            <div className="mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="text-center p-2 bg-[#F4F6F8] rounded-md">
                  <p className="text-[10px] text-[#94A3B8]">Média</p>
                  <p className="text-sm font-bold text-[#0F1C2E]">{formatBRL(comparative.stats?.media)}</p>
                </div>
                <div className="text-center p-2 bg-[#F4F6F8] rounded-md">
                  <p className="text-[10px] text-[#94A3B8]">Mediana</p>
                  <p className="text-sm font-bold text-[#0F1C2E]">{formatBRL(comparative.stats?.mediana)}</p>
                </div>
                <div className="text-center p-2 bg-[#F4F6F8] rounded-md">
                  <p className="text-[10px] text-[#94A3B8]">Maior</p>
                  <p className="text-sm font-bold text-[#C62828]">{formatBRL(comparative.stats?.maior)}</p>
                </div>
                <div className="text-center p-2 bg-[#F4F6F8] rounded-md">
                  <p className="text-[10px] text-[#94A3B8]">Menor</p>
                  <p className="text-sm font-bold text-[#2E7D32]">{formatBRL(comparative.stats?.menor)}</p>
                </div>
              </div>
              {compChartData.length > 0 && (
                <ResponsiveContainer width="100%" height={Math.max(200, compChartData.length * 30)}>
                  <BarChart data={compChartData} layout="vertical" margin={{ left: 5, right: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tickFormatter={v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}k` : v} tick={{ fontSize: 10, fill: '#94A3B8' }} />
                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 10, fill: '#475569' }} />
                    <Tooltip formatter={v => [formatBRL(v), 'Patrimônio']} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 11 }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                      {compChartData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {comparative.by_party?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-bold text-[#0F1C2E] mb-2">Patrimônio Médio por Partido</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {comparative.by_party.slice(0, 12).map((p, idx) => (
                      <div key={idx} className="p-2 bg-[#FAFBFC] border border-[#E2E8F0] rounded-md text-center">
                        <p className="text-xs font-bold text-[#006030]">{p.partido}</p>
                        <p className="text-xs text-[#0F1C2E] font-medium">{formatBRL(p.media)}</p>
                        <p className="text-[10px] text-[#94A3B8]">{p.candidatos} cand.</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Empty State */}
      {searched && !loading && alerts.length === 0 && (
        <Card className="p-8 bg-white border border-[#E2E8F0] rounded-md text-center" data-testid="fdo-empty">
          <Scale className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" />
          <h3 className="text-lg font-bold text-[#0F1C2E] mb-1">Nenhum padrão atípico identificado</h3>
          <p className="text-sm text-[#475569]">Os candidatos analisados não apresentaram padrões patrimoniais fora da média.</p>
        </Card>
      )}
    </div>
  );
};

export default FiqueDeOlho;
